import { type ReactNode, type HTMLAttributes } from 'react';

// 卡片变体类型
type CardVariant = 'default' | 'navigation' | 'highlight';

// Card组件属性接口
interface CardProps extends HTMLAttributes<HTMLDivElement> {
  // 卡片标题
  title?: string;
  // 主要内容
  children: ReactNode;
  // 卡片变体类型
  variant?: CardVariant;
  // 是否可点击
  clickable?: boolean;
  // 点击处理函数
  onClick?: () => void;
  // 底部操作区域
  footer?: ReactNode;
  // 自定义样式类
  className?: string;
}

/**
 * Card 可复用卡片组件
 * 支持标题、内容、操作区域的模块化组合
 * 遵循王者荣耀设计规范，支持多种使用场景
 */
export function Card({
  title,
  children,
  variant = 'default',
  clickable = false,
  onClick,
  footer,
  className = '',
  ...props
}: CardProps) {
  // 构建基础样式类名
  const baseClasses = [
    'bg-white',
    'border',
    'border-gray-200',
    'rounded-xl', // 12px 圆角
    'shadow-sm',
    'transition-all',
    'duration-200',
    'ease-in-out',
  ];

  // 变体样式
  const variantClasses = [];
  if (variant === 'navigation') {
    variantClasses.push(
      'text-center',
      'cursor-pointer',
      'hover:border-yellow-600',
      'hover:shadow-md',
      'hover:-translate-y-1',
      'active:transform',
      'active:scale-[0.98]'
    );
  } else if (variant === 'highlight') {
    variantClasses.push(
      'border-yellow-200',
      'bg-gradient-to-br',
      'from-white',
      'to-yellow-50'
    );
  } else {
    // default 变体
    variantClasses.push('hover:border-yellow-300', 'hover:shadow-md');
  }

  // 可点击样式
  const clickableClasses = [];
  if (clickable || onClick) {
    clickableClasses.push(
      'cursor-pointer',
      'hover:border-yellow-600',
      'hover:shadow-lg',
      'hover:-translate-y-0.5',
      'active:transform',
      'active:scale-[0.98]'
    );
  }

  // 内边距样式
  const paddingClasses = variant === 'navigation' ? ['p-6'] : ['p-6'];

  // 合并所有样式类
  const allClasses = [
    ...baseClasses,
    ...variantClasses,
    ...clickableClasses,
    ...paddingClasses,
    className,
  ].filter(Boolean);

  const cardClasses = allClasses.join(' ');

  // 处理点击事件
  const handleClick = () => {
    if (clickable || onClick) {
      onClick?.();
    }
  };

  return (
    <div
      className={cardClasses}
      onClick={handleClick}
      role={clickable || onClick ? 'button' : undefined}
      tabIndex={clickable || onClick ? 0 : undefined}
      onKeyDown={e => {
        if ((clickable || onClick) && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          handleClick();
        }
      }}
      {...props}
    >
      {/* 标题区域 */}
      {title && (
        <div className='mb-4'>
          <h4 className='text-lg font-medium text-gray-900'>{title}</h4>
        </div>
      )}

      {/* 主要内容区域 */}
      <div className={footer ? 'mb-4' : ''}>{children}</div>

      {/* 底部操作区域 */}
      {footer && <div className='pt-4 border-t border-gray-100'>{footer}</div>}
    </div>
  );
}

// 导出类型供外部使用
export type { CardProps, CardVariant };
